import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { Users, ExternalLink, CalendarClock, Bell } from "lucide-react";

export default function CandidateFilings() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/public/ontario-candidates/latest").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);

  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-2xl">
        <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 mb-4">
          <Users className="h-3 w-3 mr-1" /> Latest Candidate Filings
        </Badge>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="filings-title">
          Newest candidates across Ontario.
        </h1>
        <p className="text-muted-foreground mt-3 leading-relaxed">
          Every day at <span className="font-semibold text-foreground">6:00 PM Eastern</span>, Witness Your Vote scans
          official municipal election sources for newly listed candidates. Records are imported with their source link,
          marked unverified until reviewed, and never invented.
        </p>
      </div>

      <div className="mt-10 space-y-3" data-testid="filings-list">
        {!rows && [1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
        {rows?.map((c) => (
          <Card key={c.id} data-testid={`filing-${c.id}`}>
            <CardContent className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-heading font-semibold text-base text-foreground">{c.full_name}</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {c.municipality_name}{c.office ? ` · ${c.office}` : ""}{c.ward ? ` · ${c.ward}` : ""}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-700 border-slate-300">
                      {c.data_status === "imported_from_official_source" ? "Imported from official source" : c.data_status}
                    </Badge>
                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">
                      {c.verification_status === "admin_verified" ? "Admin verified" : "Unverified — confirm with official source"}
                    </Badge>
                    {c.filing_status && <Badge variant="outline" className="text-[10px]">{c.filing_status}</Badge>}
                  </div>
                </div>
                <div className="text-right text-xs text-muted-foreground shrink-0">
                  <a href={c.source_url} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 text-primary font-medium hover:underline">
                    Official source <ExternalLink className="h-3 w-3" />
                  </a>
                  <p className="mt-1.5 inline-flex items-center gap-1">
                    <CalendarClock className="h-3 w-3" />
                    Checked {c.last_checked_at ? new Date(c.last_checked_at).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {rows && rows.length === 0 && (
          <Card data-testid="filings-empty">
            <CardContent className="p-8 text-center">
              <Users className="h-8 w-8 text-slate-300 mx-auto mb-3" />
              <h2 className="font-heading font-semibold text-base text-foreground">No imported candidate filings yet</h2>
              <p className="text-sm text-muted-foreground mt-2 max-w-lg mx-auto leading-relaxed">
                Ontario's 2026 municipal nomination periods have not yet opened, so official candidate lists are not
                yet published. The daily scan runs at 6:00 PM Eastern and new filings will appear here automatically
                once municipalities publish their official lists.
              </p>
              <Button asChild className="mt-5 bg-accent hover:bg-accent/90" data-testid="filings-alerts-cta">
                <Link to="/alerts"><Bell className="h-4 w-4 mr-1.5" /> Get Alerted About New Candidates</Link>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <Card className="mt-8 bg-secondary max-w-3xl">
        <CardContent className="p-5 text-xs text-muted-foreground leading-relaxed">
          Candidate information is collected only from official municipal election sources, with the source link kept
          on every record. Records are marked unverified until reviewed. For official candidate lists, always consult
          your municipal Clerk's office.
        </CardContent>
      </Card>
    </div>
  );
}
